import { useState } from 'react';
import { Checkbox, Label } from '@shared/ui';
import { useUserStore } from '../../../auth/store/userStore';
import { updateFirmMfa } from '../../services/profileService';
import { checkboxLabelClassName } from '@shared/ui/formStyles';

export function MfaSection({ onLoadingChange, dataTestId }) {
  const { profile, updateProfile: updateStoreProfile } = useUserStore();
  const [isSaving, setIsSaving] = useState(false);

  const mfaEnabled = !!profile?.firm?.mfaEnabled;
  const adminAllowed = !!profile?.firm?.isMfaAllowed;
  const readOnly = !adminAllowed; // editable only while the admin permits MFA
  const baseTestId = dataTestId || 'profile-mfa-section';

  const handleToggle = async (checked) => {
    const next = !!checked;
    if (readOnly || next === mfaEnabled || isSaving) return;

    setIsSaving(true);
    // Show the full profile-popup loader (same overlay as "Save Changes") instead of a
    // small inline spinner, so the whole panel is blocked while the toggle is applied.
    onLoadingChange?.(true, 'Updating MFA...');
    try {
      const result = await updateFirmMfa(next);
      if (result?.success) {
        updateStoreProfile({ firm: { ...profile.firm, mfaEnabled: next } });
      }
    } finally {
      setIsSaving(false);
      onLoadingChange?.(false);
    }
  };

  return (
    <div className="flex justify-start" data-testid={baseTestId}>
      {readOnly ? (
        // Condition 5: admin has disallowed MFA — no control, just a short note.
        <p className="text-xs font-semibold text-fg-secondary" data-testid={`${baseTestId}-notice`}>
          MFA is disabled for this firm. Contact admin for more information.
        </p>
      ) : (
        <div className="flex items-center gap-2" data-testid={`${baseTestId}-toggle-container`}>
          <Checkbox
            id="mfaEnabled"
            checked={mfaEnabled}
            onCheckedChange={handleToggle}
            disabled={isSaving}
            data-testid={`${baseTestId}-checkbox`}
          />
          {/* The app's checkbox-label style (as "Same as billing address"), not the field-label grey. */}
          <Label htmlFor="mfaEnabled" className={checkboxLabelClassName} data-testid={`${baseTestId}-label`}>
            Require MFA for all users
          </Label>
        </div>
      )}
    </div>
  );
}
